import { Injectable } from '@angular/core';
import { EMPTY_ARRAY_LENGTH, NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    IEquity,
    IEquityEntry,
    IFinancialLineItemComponent,
    IFli,
    IFreeTable,
    IFreeTableEntry,
    IManualAccount,
    IMovementScheduleAccount,
    IMovementScheduleComponent,
    INotesComponent,
    INotesInputComponent,
    INotesSection,
    INotesSelectComponent,
    ISuperTableComponent,
    ISuperTableRow,
    Notes,
    SuperTableRowType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import { ComponentType, IRichText, SuperTableColumnType } from '@modeso/types__aumico-lib-notes-be';
import { cloneDeep } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class NotesEmptyLinesRemovalHelper {
    public removeEmptyLinesOfNotes(accordions: INotesSection[], reportLocale: string): INotesSection[] {
        const accordionWithRemovedEmptyLine: INotesSection[] = [];

        for (const accordion of accordions) {
            const accordionWithRemovedEmptyLines = this.removeEmptyLinesOfAccordion(accordion, reportLocale);
            const isAccordionEmptyOfComponents = accordionWithRemovedEmptyLines.data.length === 0;

            if (isAccordionEmptyOfComponents) {
                continue;
            }

            accordionWithRemovedEmptyLine.push(accordionWithRemovedEmptyLines);
        }

        return accordionWithRemovedEmptyLine;
    }

    private removeEmptyLinesOfAccordion(accordion: INotesSection, reportLocale: string): INotesSection {
        const accordionWithRemovedEmptyLines = cloneDeep<INotesSection>(accordion);
        const accordionDataWithRemovedEmptyLine: INotesComponent[] = [];



        for (const accordionData of accordion.data) {

            const isComponentDataEmpty = this.isComponentDataEmpty(
                accordionData.componentName,
                accordionData.data,
                reportLocale,
            );

            if (isComponentDataEmpty) {
                continue;
            }

            const componentDataWithRemovedEmptyLines = this.removeEmptyLinesOfComponents(
                accordionData.componentName,
                accordionData.data,
            );
            const isComponentDataAfterRemovingRowsEmpty = this.isComponentDataEmpty(
                accordionData.componentName,
                componentDataWithRemovedEmptyLines,
                reportLocale,
            );

            if (isComponentDataAfterRemovingRowsEmpty) {
                continue;
            }
            accordionDataWithRemovedEmptyLine.push({
                componentName: accordionData.componentName,
                componentTitle: accordionData.componentTitle,
                componentId: accordionData.componentId,
                data: componentDataWithRemovedEmptyLines,
                addNumbering: accordionData.addNumbering,
                referenceFlis: accordionData.referenceFlis,
                hasFootnotes: accordionData.hasFootnotes,
                isHidden: accordionData.isHidden,
            });
        }

        accordionWithRemovedEmptyLines.data = [...accordionDataWithRemovedEmptyLine];

        return accordionWithRemovedEmptyLines;
    }

    public isComponentDataEmpty(componentName: ComponentType, data: Notes, reportLocale: string): boolean {
        if (data == null) {
            return true;
        }
        switch (componentName) {
            case ComponentType.RICH_TEXT:
                return this.isRichTextEmpty(data as IRichText[], reportLocale);

            case ComponentType.EQUITY:
                return this.isEquityEmpty(data as IEquity);

            case ComponentType.FINANCIAL_LINE_ITEM:
                return this.isFliComponentEmpty(data as IFinancialLineItemComponent);

            case ComponentType.FREE_TABLE_WITH_AMOUNTS:
                return this.isFreeTableEmpty(data as IFreeTable);

            case ComponentType.SELECT:
                return this.isSelectComponentEmpty(data as INotesSelectComponent);

            case ComponentType.INPUT:
                return this.isInputComponentEmpty(data as INotesInputComponent);

            case ComponentType.MOVEMENT_SCHEDULE:
                return this.isMovementScheduleEmpty(data as IMovementScheduleComponent);

            case ComponentType.SUPER_TABLE:
                return this.isSuperTableEmpty(data as ISuperTableComponent);

            default:
                throw new Error(`This component ${componentName} is not supported`);
        }
    }

    removeEmptyLinesOfComponents(componentName: ComponentType, data: Notes): Notes {
        switch (componentName) {
            case ComponentType.RICH_TEXT:
            case ComponentType.SELECT:
            case ComponentType.INPUT:
                return cloneDeep<Notes>(data);

            case ComponentType.EQUITY:
                return this.removeEmptyRowsOfEquity(data as IEquity);

            case ComponentType.FINANCIAL_LINE_ITEM:
                return this.removeEmptyRowsOfFli(data as IFinancialLineItemComponent);

            case ComponentType.FREE_TABLE_WITH_AMOUNTS:
                return this.removeEmptyRowsOfFreeTable(data as IFreeTable);

            case ComponentType.MOVEMENT_SCHEDULE:
                return this.removeEmptyRowsOfMovementSchedule(data as IMovementScheduleComponent);

            case ComponentType.SUPER_TABLE:
                return this.removeEmptyRowsOfSuperTable(data as ISuperTableComponent);

            default:
                throw new Error(`This component ${componentName} is not supported`);
        }
    }

    private isRichTextEmpty(data: IRichText[], reportLocale: string): boolean {
        if (data == null || !Array.isArray(data)) {
            return true;
        }
        const foundRichTextByLanguage = data.find((richTextData) => richTextData.language === reportLocale);
        const htmlTagsHasNoContentInsideRegEx = /<\/?[^>]+(>|$)/g;
        return (
            foundRichTextByLanguage == null ||
            foundRichTextByLanguage.text == null ||
            foundRichTextByLanguage.text.replace(htmlTagsHasNoContentInsideRegEx, '') == ''
        );
    }

    private removeEmptyRowsOfEquity(data: IEquity): IEquity {
        const printPreviewDataWithRemovedEmptyLines = cloneDeep<IEquity>(data);
        printPreviewDataWithRemovedEmptyLines.rows = [];
        for (const row of data.rows) {
            const isCurrentValue1Empty = row.currentValue1 == null || (row.currentValue1 as any) === '';

            const isCurrentValues2Empty = row.currentValue2 == null || (row.currentValue2 as any) === '';

            const isPreviousValues1Empty = row.previousValue1 == null || (row.previousValue1 as any) === '';
            const isPreviousValues2Empty = row.previousValue2 == null || (row.previousValue2 as any) === '';

            const isRowEmpty =
                isCurrentValue1Empty && isCurrentValues2Empty && isPreviousValues1Empty && isPreviousValues2Empty;

            if (isRowEmpty) {
                continue;
            }

            const nonEmptyRow = cloneDeep<IEquityEntry>(row);
            printPreviewDataWithRemovedEmptyLines.rows.push(nonEmptyRow);
        }

        return printPreviewDataWithRemovedEmptyLines;
    }

    private isEquityEmpty(data: IEquity): boolean {
        const currentValues1 = data.rows.map((row) => row.currentValue1);
        const currentValues2 = data.rows.map((row) => row.currentValue2);
        const previousValues1 = data.rows.map((row) => row.previousValue1);
        const previousValues2 = data.rows.map((row) => row.previousValue2);

        const currentValues1Empty = currentValues1.every(
            (value) => value === null || value === undefined || (value as any) === '',
        );
        const currentValues2Empty = currentValues2.every(
            (value) => value === null || value === undefined || (value as any) === '',
        );
        const previousValues1Empty = previousValues1.every(
            (value) => value === null || value === undefined || (value as any) === '',
        );
        const previousValues2Empty = previousValues2.every(
            (value) => value === null || value === undefined || (value as any) === '',
        );

        return (
            data.rows.length === 0 ||
            (currentValues1Empty && currentValues2Empty && previousValues1Empty && previousValues2Empty)
        );
    }

    private removeEmptyRowsOfFli(data: IFinancialLineItemComponent): IFinancialLineItemComponent {
        const printPreviewDataWithRemovedEmptyLines = cloneDeep<IFinancialLineItemComponent>(data);
        printPreviewDataWithRemovedEmptyLines.flis = [];
        for (const fli of data.flis) {
            const currentFli = cloneDeep<IFli>(fli);
            const accounts: IManualAccount[] = [];

            if (fli != null && fli.accounts != null) {
                for (const account of fli.accounts) {
                    const isCurrentValueEmpty =
                        !NumberUtils.isFinite(account.currentValue) || NumberUtils.isZero(account.currentValue);

                    const isPreviousValueEmpty =
                        !NumberUtils.isFinite(account.previousValue) || NumberUtils.isZero(account.previousValue);

                    const isRowEmpty = isCurrentValueEmpty && isPreviousValueEmpty;

                    if (isRowEmpty) {
                        continue;
                    }

                    const nonEmptyAccountRow = cloneDeep<IManualAccount>(account);
                    accounts.push(nonEmptyAccountRow);
                }
            }

            if (accounts.length === 0) {
                continue;
            }

            currentFli.accounts = [...accounts];
            printPreviewDataWithRemovedEmptyLines.flis.push(currentFli);
        }

        return printPreviewDataWithRemovedEmptyLines;
    }

    private removeEmptyRowsOfMovementSchedule(data: IMovementScheduleComponent): IMovementScheduleComponent {
        const printPreviewDataWithRemovedEmptyLines = cloneDeep<IMovementScheduleComponent>(data);
        printPreviewDataWithRemovedEmptyLines.flis = [];
        for (const fli of data.flis) {
            const currentFli = cloneDeep<IFli>(fli);
            const accounts: IManualAccount[] = [];

            for (const account of fli.accounts) {
                const isCurrentValueEmpty =
                    !NumberUtils.isFinite(account.closeValue) || NumberUtils.isZero(account.closeValue);

                const isPreviousValueEmpty =
                    !NumberUtils.isFinite(account.openValue) || NumberUtils.isZero(account.openValue);

                const isColumn1Empty = !NumberUtils.isFinite(account.column1) || NumberUtils.isZero(account.column1);

                const isColumn2Empty = !NumberUtils.isFinite(account.column2) || NumberUtils.isZero(account.column2);

                const isColumn3Empty = !NumberUtils.isFinite(account.column3) || NumberUtils.isZero(account.column3);

                const isColumn4Empty = !NumberUtils.isFinite(account.column4) || NumberUtils.isZero(account.column4);

                const isRowEmpty =
                    isCurrentValueEmpty &&
                    isPreviousValueEmpty &&
                    isColumn1Empty &&
                    isColumn2Empty &&
                    isColumn3Empty &&
                    isColumn4Empty;

                if (isRowEmpty) {
                    continue;
                }

                const nonEmptyAccountRow = cloneDeep<IMovementScheduleAccount>(account);
                accounts.push(nonEmptyAccountRow);
            }

            if (accounts.length === 0) {
                continue;
            }

            currentFli.accounts = [...accounts];
            printPreviewDataWithRemovedEmptyLines.flis.push(currentFli);
        }

        return printPreviewDataWithRemovedEmptyLines;
    }

    private isFliComponentEmpty(data: IFinancialLineItemComponent): boolean {
        return data.flis == null || data.flis.length === 0;
    }

    private removeEmptyRowsOfSuperTable(data: ISuperTableComponent): ISuperTableComponent {
        const superTableData: ISuperTableComponent = cloneDeep<ISuperTableComponent>(data);
        let nonEmptyRows: ISuperTableRow[] = [];
        // FX: hiding the functional previous-year column deletes `previousValue` from the rows. When
        // that year is still shown as its display-currency twin, a computed row's previous year
        // cannot be judged here — keep the row rather than let it (and maybe its table) vanish.
        const columnIds = new Set(data.options.xAxisConfig.map((col) => col.columnId));
        const previousYearOnlyAsFxTwin =
            columnIds.has(SuperTableColumnType.PREVIOUS_YEAR_FX) && !columnIds.has(SuperTableColumnType.PREVIOUS_YEAR);
        for (const row of data.rows) {
            if (row.type === SuperTableRowType.SPACE || row.type === SuperTableRowType.TITLE) {
                nonEmptyRows.push(row);
                continue;
            }

            // FX (REV-21512): rate rows never store values — their cells are computed at render
            // time, so the emptiness check below would always (wrongly) drop them.
            if (row.type === SuperTableRowType.RATE) {
                nonEmptyRows.push(row);
                continue;
            }

            // Check if this is a FLI-based row type that should be removed when all values are empty/zero
            const isFliBasedRow =
                row.type === SuperTableRowType.FLI ||
                row.type === SuperTableRowType.SUB_FLI ||
                row.type === SuperTableRowType.OF_THAT_ITEM ||
                row.type === SuperTableRowType.ACCOUNT;

            if (isFliBasedRow) {
                const isCurrentValueEmpty =
                    row.currentValue == null || (typeof row.currentValue === 'number' && NumberUtils.isZero(row.currentValue));

                const isPreviousValueEmpty =
                    row.previousValue == null ||
                    (typeof row.previousValue === 'number' && NumberUtils.isZero(row.previousValue));
                if (isCurrentValueEmpty && isPreviousValueEmpty && !previousYearOnlyAsFxTwin) {
                    continue;
                }
            }

            for (const col of data.options.xAxisConfig) {
                // FX: a computed row's display twins are not stored on the row — they translate its
                // functional figures, so they are filled exactly when those are.
                if (isFliBasedRow && col.columnId === SuperTableColumnType.CURRENT_YEAR_FX) {
                    if (row.currentValue != null && (row.currentValue as any) !== '') {
                        nonEmptyRows.push(row);
                        break;
                    }
                    continue;
                }
                if (isFliBasedRow && col.columnId === SuperTableColumnType.PREVIOUS_YEAR_FX) {
                    if (previousYearOnlyAsFxTwin || (row.previousValue != null && (row.previousValue as any) !== '')) {
                        nonEmptyRows.push(row);
                        break;
                    }
                    continue;
                }
                if (col.columnId === SuperTableColumnType.CURRENT_YEAR) {
                    if (row.currentValue != null && (row.currentValue as any) !== '') {
                        nonEmptyRows.push(row);
                        break;
                    }
                }
                if (col.columnId === SuperTableColumnType.PREVIOUS_YEAR) {
                    if (row.previousValue != null && (row.previousValue as any) !== '') {
                        nonEmptyRows.push(row);
                        break;
                    }
                }

                if (row[col.columnId] != null && row[col.columnId] !== '') {
                    nonEmptyRows.push(row);

                    break;
                }
            }
        }

        if (nonEmptyRows.length !== EMPTY_ARRAY_LENGTH) {
            const allRowsAreCalculation = nonEmptyRows.every((row) => row.type === SuperTableRowType.CALCULATION);
            if (allRowsAreCalculation) {
                nonEmptyRows = [];
            }
        }

        superTableData.rows = nonEmptyRows;

        return superTableData;
    }

    private isMovementScheduleEmpty(data: IMovementScheduleComponent): boolean {
        return data.flis.length === 0;
    }

    private isSuperTableEmpty(data: ISuperTableComponent): boolean {
        return data.rows.length === EMPTY_ARRAY_LENGTH;
    }

    private removeEmptyRowsOfFreeTable(data: IFreeTable): IFreeTable {
        const printPreviewDataWithRemovedEmptyLines = cloneDeep<IFreeTable>(data);
        printPreviewDataWithRemovedEmptyLines.rows = [];
        for (const row of data.rows) {
            const isCurrentValueEmpty = row.currentValue == null || (row.currentValue as any) === '';

            const isPreviousValueEmpty = row.previousValue == null || (row.previousValue as any) === '';
            const isRowEmpty = (row.text == null || row.text === '') && isCurrentValueEmpty && isPreviousValueEmpty;

            if (isRowEmpty) {
                continue;
            }

            const nonEmptyRow = cloneDeep<IFreeTableEntry>(row);
            printPreviewDataWithRemovedEmptyLines.rows.push(nonEmptyRow);
        }

        return printPreviewDataWithRemovedEmptyLines;
    }

    private isFreeTableEmpty(data: IFreeTable): boolean {
        const currentValues = data.rows.map((row) => row.currentValue);
        const previousValues = data.rows.map((row) => row.previousValue);

        const currentValuesEmpty = currentValues.every(
            (value) => value === null || value === undefined || (value as any) === '',
        );
        const previousValuesEmpty = previousValues.every(
            (value) => value === null || value === undefined || (value as any) === '',
        );

        return data.rows.length === 0 || (currentValuesEmpty && previousValuesEmpty);
    }

    private isSelectComponentEmpty(data: INotesSelectComponent): boolean {
        return data.currentYearValue == null && data.previousYearValue == null;
    }

    private isPropertyEmpty(prop: IDynamicKeyValue | undefined): boolean {
        if (prop == null) {
            return true;
        }

        for (const value of Object.values(prop)) {
            if (value != null && value !== '') {
                return false;
            }
        }

        return true;
    }

    private isInputComponentEmpty(data: INotesInputComponent): boolean {
        return data.currentYearValue == null && data.previousYearValue == null;
    }

}
